import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useEffect, useMemo, useState } from 'react';
import theme from 'src/assets/theme/theme';
import { useApplication } from 'src/context/ApplicationContext';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { KOSTEN_ARTEN, KOSTEN_POSITION, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import { removeDataKeys, removeNullDeep } from 'src/utils/helper';

// Schemas
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import NachweisPositionSchema from 'src/lib/paths/nachweisposition_FULL_Paths.json';
import VerwendungsnachweisSchema from 'src/lib/paths/verwendungsnachweis_FULL_Paths.json';
const KP_P = (KostenPositionSchema as any).attributes;
const VN_P = (VerwendungsnachweisSchema as any).attributes;
const NP_P = (NachweisPositionSchema as any).attributes;

const VNFinanzierungsPlan: FC<{}> = () => {
	const { application } = useApplication();
	const [allKostenPositions, setAllKostenPositions] = useState<any[]>([]);

	const haushaltsJahr = get(application, VN_P.Allgemein_Haushaltsjahr.path);

	const allNachweisPosition = useMemo(() => get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []), [application]);

	const data = useMemo(
		() => transformData(allNachweisPosition, allKostenPositions, haushaltsJahr),
		[allNachweisPosition, allKostenPositions, haushaltsJahr]
	);

	useEffect(() => {
		const fetch = async () => {
			try {
				const filter = {
					Foerderantrag: { id: { $eq: get(application, `${VN_P.Foerderantrag.path}.id`) } },
				};
				const res = await GetRecords(APIS.Kostenposition, ['Allgemein_Kosten_Pro_Jahr'], filter);
				if (res) setAllKostenPositions(removeNullDeep(removeDataKeys(get(res, 'data', []))));
			} catch (err) {
				console.error('ERROR: Fetching KostenPosition ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Ausgaben- und Finanzierungsplan' description={PLATZHALTER_TEXT} />
			<Typography variant='body1' sx={{ mb: 2 }}>
				Zahlenmäßiger Nachweis der Finanzierung des Vorhabens
			</Typography>
			<Box sx={{ mt: '2rem' }}>
				<TableContainer>
					<Table aria-label='Ausgaben- und Finanzierungsplan'>
						<TableHead>
							<TableRow>
								<TableCell></TableCell>
								<TableCell align='right'>BEWILLIGT (gemäß Antrag)</TableCell>
								<TableCell align='right'>AKTUELLER ABRUF (gemäß Nachweis)</TableCell>
								<TableCell align='right'>NOCH OFFEN (lt. Bescheid)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.rows.map((row) => {
								const isSummary = row.type !== 'base';
								const isQuote = row.type === 'quote';
								return (
									<TableRow key={row.name} sx={isSummary ? { backgroundColor: theme.palette.background.paper } : undefined}>
										<TableCell>{row.name}</TableCell>
										<TableCell align='right'>{isQuote ? DisplayDecimal(row.soll, true) : DisplayEuro(row.soll)}</TableCell>
										<TableCell align='right'>{isQuote ? DisplayDecimal(row.ist, true) : DisplayEuro(row.ist)}</TableCell>
										<TableCell align='right'>{isQuote ? DisplayDecimal(row.verfuegbar, true) : DisplayEuro(row.verfuegbar)}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Box>
		</>
	);
};

export default VNFinanzierungsPlan;

interface Row {
	name: string;
	soll: number;
	ist: number;
	verfuegbar: number;
	type: 'base' | 'sum' | 'diff' | 'quote';
}

const transformData = (allNachweis: any[], allKostenPositions: any[], haushaltsJahr: number) => {
	// Nachweise filtern
	const nachweisInvest = allNachweis.filter(
		(n: any) => get(n, NP_P.Allgemein_Kostenposition.schema.attributes.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Investitionskosten
	);
	const nachweisEigen = allNachweis.filter(
		(n: any) => get(n, NP_P.Allgemein_Kostenposition.schema.attributes.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Eigenmittel
	);

	// Basiswerte (immer berechnen, auch wenn leer)
	const sollInvest = getGesamtKostenProKostenArtProJahr(allKostenPositions, KOSTEN_ARTEN.Investitionskosten, haushaltsJahr);
	const istInvest = nachweisInvest.reduce((s: number, n: any) => s + parseFloat(get(n, NP_P.Allgemein_Ausgaben_Kosten.path, '0') || '0'), 0);
	const verfuegbarInvest = sollInvest - istInvest;

	const sollEigen = getGesamtKostenProJahr(allKostenPositions, KOSTEN_POSITION.Einnahme, haushaltsJahr);
	const istEigen = nachweisEigen.reduce((s: number, n: any) => s + parseFloat(get(n, NP_P.Allgemein_Ausgaben_Kosten.path, '0') || '0'), 0);
	const verfuegbarEigen = sollEigen - istEigen;

	// Gesamtausgaben = Invest + Eigen (spaltenweise)
	const gesSoll = sollInvest + sollEigen;
	const gesIst = istInvest + istEigen;
	const gesVerf = verfuegbarInvest + verfuegbarEigen;

	// Förderung (€) = Investitionskosten - Eigenmittel (spaltenweise)
	const foerdSoll = sollInvest - sollEigen;
	const foerdIst = istInvest - istEigen;
	const foerdVerf = verfuegbarInvest - verfuegbarEigen;

	// Förderquote (%) = Förderung / Investitionskosten * 100 (spaltenweise)
	const quote = (base: number, invest: number) => (invest > 0 ? (base / invest) * 100 : 0);
	const quoteSoll = quote(foerdSoll, sollInvest);
	const quoteIst = quote(foerdIst, istInvest);
	const quoteVerf = quote(foerdVerf, verfuegbarInvest);

	const rows: Row[] = [
		{
			name: KOSTEN_ARTEN.Investitionskosten,
			soll: round2(sollInvest),
			ist: round2(istInvest),
			verfuegbar: round2(verfuegbarInvest),
			type: 'base',
		},
		{
			name: 'Eigen- und Drittmittel',
			soll: round2(sollEigen),
			ist: round2(istEigen),
			verfuegbar: round2(verfuegbarEigen),
			type: 'base',
		},
		{
			name: 'Gesamtausgaben',
			soll: round2(gesSoll),
			ist: round2(gesIst),
			verfuegbar: round2(gesVerf),
			type: 'sum',
		},
		{
			name: 'Förderung (€)',
			soll: round2(foerdSoll),
			ist: round2(foerdIst),
			verfuegbar: round2(foerdVerf),
			type: 'diff',
		},
		{
			name: 'Förderquote',
			soll: round2(quoteSoll),
			ist: round2(quoteIst),
			verfuegbar: round2(quoteVerf),
			type: 'quote',
		},
	];

	return { rows };
};

function getGesamtKostenProKostenArtProJahr(allkostenPOsition: any[], kostenArt: string, haushaltJahr: number): number {
	return allkostenPOsition
		.filter((item) => item.attributes.Allgemein_Kostenart_Finanzierung === kostenArt)
		.reduce((sum, item) => {
			const jahrKosten = item.attributes.Allgemein_Kosten_Pro_Jahr.find((jahr: any) => jahr.Zelle_1 === haushaltJahr);
			return sum + (jahrKosten ? jahrKosten.Zelle_2 : 0);
		}, 0);
}

function getGesamtKostenProJahr(allkostenPOsition: any[], kostenPosition: string, haushaltJahr: number): number {
	return allkostenPOsition
		.filter((item) => get(item, KP_P.Allgemein_Kostenposition.path) === kostenPosition)
		.reduce((sum, item) => {
			const jahrKosten = item.attributes.Allgemein_Kosten_Pro_Jahr.find((jahr: any) => jahr.Zelle_1 === haushaltJahr);
			return sum + (jahrKosten ? jahrKosten.Zelle_2 : 0);
		}, 0);
}

function round2(n: number) {
	return Math.round((n + Number.EPSILON) * 100) / 100;
}
